import * as React from 'react';

import { cva } from 'class-variance-authority';
import { cn } from './utils';

import type { VariantProps } from 'class-variance-authority';

const surfaceVariants = cva('min-h-4 min-w-4 transition duration-300', {
  variants: {
    variant: {
      default: 'bg-white/10 hover:bg-white/25',
      panel: 'bg-layer-1',
      elevated: 'bg-layer-1 shadow-lg',
      outline: 'bg-transparent ring-[1px] ring-inset ring-border',
      ghost: 'bg-transparent hover:bg-white/10',
    },
    radius: {
      none: 'rounded-none',
      small: 'rounded-sm',
      medium: 'rounded-md',
      large: 'rounded-lg',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    variant: 'default',
    radius: 'medium',
  },
});

type NativeTagName = 'div' | 'button' | 'a';

type NativeElementProps<T extends NativeTagName> = T extends 'div'
  ? React.HTMLAttributes<HTMLDivElement>
  : T extends 'button'
    ? React.ButtonHTMLAttributes<HTMLButtonElement>
    : T extends 'a'
      ? React.AnchorHTMLAttributes<HTMLAnchorElement>
      : never;

type NativeElement<T extends NativeTagName> = T extends 'div'
  ? HTMLDivElement
  : T extends 'button'
    ? HTMLButtonElement
    : T extends 'a'
      ? HTMLAnchorElement
      : never;

type SurfaceProps<T extends NativeTagName = 'div'> = NativeElementProps<T> &
  VariantProps<typeof surfaceVariants> & {
    as?: T;
    showBorder?: boolean;
    borderColor?: string;
  };

function SurfaceInner<T extends NativeTagName = 'div'>(
  { as, variant, radius, showBorder, borderColor = 'ring-border', className, onClick, children, ...props }: SurfaceProps<T>,
  ref: React.ForwardedRef<NativeElement<T>>,
) {
  const Comp = (as ?? 'div') as React.ElementType;
  const cursor = onClick ? 'cursor-pointer' : '';
  const border = showBorder ? cn('ring-[1px] ring-inset', borderColor) : '';

  return (
    <Comp ref={ref} className={cn(surfaceVariants({ variant, radius }), cursor, border, className)} onClick={onClick} {...props}>
      {children}
    </Comp>
  );
}

const Surface = React.forwardRef(SurfaceInner) as <T extends NativeTagName = 'div'>(
  props: SurfaceProps<T> & { ref?: React.ForwardedRef<NativeElement<T>> },
) => React.ReactElement;

(Surface as React.FC).displayName = 'Surface';

export { Surface, surfaceVariants };
export type { NativeTagName, SurfaceProps };
